import { FC, memo, useMemo, useCallback } from "react";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import { useGetSuggestedTopicsQuery } from "@/generated/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import { MAX_INTENT_TOPICS_FOR_SUGGESTIONS, type CachedTopic } from "../slice";

type TopicSuggestionsProps = {
  selectedTopicIds: string[];
  onAddTopic: (topic: CachedTopic) => void;
};

const MAX_SUGGESTIONS = 5;

/**
 * Displays suggested topics based on the currently selected topics.
 * Uses the GraphQL suggestedTopic query to get related topics.
 */
export const TopicSuggestions: FC<TopicSuggestionsProps> = memo(
  ({ selectedTopicIds, onAddTopic }) => {
    const hasSelectedTopics = selectedTopicIds.length > 0;
    const isDisabled =
      selectedTopicIds.length > MAX_INTENT_TOPICS_FOR_SUGGESTIONS;

    const { data, loading } = useGetSuggestedTopicsQuery({
      variables: { ids: selectedTopicIds },
      skip: !hasSelectedTopics || isDisabled,
      context: { clientName: TAXONOMY_CLIENT },
    });

    // Transform suggestions, filter out already selected, limit to MAX_SUGGESTIONS
    const suggestions = useMemo((): CachedTopic[] => {
      if (!data?.suggestedTopic?.data) return [];

      return data.suggestedTopic.data
        .filter(
          (
            item
          ): item is NonNullable<typeof item> & {
            matchingTopic: NonNullable<
              NonNullable<typeof item>["matchingTopic"]
            >;
          } =>
            item !== null &&
            item.matchingTopic !== null &&
            item.matchingTopic.id !== null &&
            item.matchingTopic.displayName !== null &&
            !selectedTopicIds.includes(item.matchingTopic.id)
        )
        .slice(0, MAX_SUGGESTIONS)
        .map((item) => ({
          id: item.matchingTopic.id as string,
          displayName: item.matchingTopic.displayName as string,
          category: undefined,
          collectionTree: [],
        }));
    }, [data, selectedTopicIds]);

    // Handler for adding a suggested topic
    const handleAdd = useCallback(
      (topic: CachedTopic) => {
        onAddTopic(topic);
      },
      [onAddTopic]
    );

    return (
      <Fade
        in={hasSelectedTopics && !isDisabled}
        timeout={200}
        mountOnEnter
        unmountOnExit
      >
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ alignItems: "center", flexShrink: 0 }}
          >
            <AutoAwesomeIcon fontSize="small" sx={{ color: "warning.main" }} />
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
            >
              Suggested:
            </Typography>
          </Stack>

          {loading && (
            <>
              {Array.from({ length: MAX_SUGGESTIONS }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  width={80 + index * 8}
                  height={24}
                />
              ))}
            </>
          )}

          {!loading && suggestions.length === 0 && (
            <Typography variant="caption" color="text.disabled">
              No suggestions
            </Typography>
          )}

          {!loading && suggestions.length > 0 && (
            <>
              {suggestions.map((topic) => (
                <SuggestionChip
                  key={topic.id}
                  topic={topic}
                  onAdd={handleAdd}
                />
              ))}
            </>
          )}
        </Stack>
      </Fade>
    );
  }
);

TopicSuggestions.displayName = "TopicSuggestions";

type SuggestionChipProps = {
  topic: CachedTopic;
  onAdd: (topic: CachedTopic) => void;
};

const SuggestionChip: FC<SuggestionChipProps> = memo(({ topic, onAdd }) => {
  const handleClick = useCallback(() => {
    onAdd(topic);
  }, [topic, onAdd]);

  return (
    <Tooltip title={topic.displayName} enterDelay={500}>
      <Chip
        label={topic.displayName}
        size="small"
        variant="outlined"
        icon={<AddIcon fontSize="small" />}
        onClick={handleClick}
        sx={{
          maxWidth: 140,
          cursor: "pointer",
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      />
    </Tooltip>
  );
});

SuggestionChip.displayName = "SuggestionChip";
